import React, { useCallback } from 'react';
import { Pannellum } from 'pannellum-react';
import styles from './DrawingPannellum.module.scss';
import ReactDOM from 'react-dom';
import { Topic } from 'src/config/topics';
import { Icon } from 'src/components/common/Icon/Icon';
import cn from 'classnames';
import { DrawingId, drawings } from 'src/config/drawings';
import { Button } from 'src/components/common/Button/Button';
import { initialDrawingId } from 'src/App';
import { Paragraph } from 'src/components/common/Paragraph/Paragraph';
import binocular from 'src/assets/images/binocular.png';
import { Drawer } from 'src/components/common/Drawer/Drawer';
import { useToggle } from 'react-use';

export type PannellumCallbackArg = Pick<Topic, 'id' | 'title' | 'type'>;

export interface DrawingPannellumProps {
  selectedDrawingId: DrawingId;
  onHotspotClick: (e: unknown, topic: PannellumCallbackArg) => void;
  onBackToClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  pannellumOptions?: Record<string, unknown>;
  hotspots: Array<Topic>;
}

export const DrawingPannellum: React.VFC<DrawingPannellumProps> = (props) => {
  const { selectedDrawingId, pannellumOptions, hotspots, onHotspotClick, onBackToClick } = props;
  const selectedDrawing = drawings.find(({ id }) => selectedDrawingId === id);
  const [drawingInfoShowing, toggleDrawingInfo] = useToggle(true);

  if (!selectedDrawing) throw new Error(`No drawing found for id: ${selectedDrawingId}`);

  const customHotspot = useCallback((hotSpotDiv: HTMLDivElement, args: PannellumCallbackArg) => {
    const { title, type } = args;
    const isScene = type === 'scene';

    ReactDOM.render((
      <div
        className={cn(styles['c-drawing-pannellum__hotspot'], {
          [styles['c-drawing-pannellum__hotspot--scene']]: isScene,
        })}
      >
        <div className={styles['c-drawing-pannellum__hotspot-marker']}>
          <Icon name={isScene ? 'LogoutIcon' : 'CursorClickIcon'} size={20} />
        </div>
        <span className={styles['c-drawing-pannellum__hotspot-text']}>{title}</span>
      </div>
    ), hotSpotDiv);
  }, []);

  return (
    <React.Fragment>
      <div className={styles['c-drawing-pannellum']}>
        {!drawingInfoShowing && (
          <div className={styles['c-drawing-pannellum__info-button']}>
            <Button tag="button" type="button" onClick={toggleDrawingInfo} variant="secondary" prefix="EyeIcon">
              Toon inleiding
            </Button>
          </div>
        )}
        {selectedDrawing.id === 'de-uitkijk' && (
          <img src={binocular} alt="Binocular view" className={styles['c-drawing-pannellum__binocular-view']} />
        )}
        <Pannellum
          autoLoad
          height="100%"
          image={selectedDrawing.image}
          keyboardZoom={false}
          hfov={selectedDrawing.hfov}
          mouseZoom={false}
          yaw={0}
          pitch={0}
          maxYaw={125}
          minYaw={-125}
          maxPitch={45}
          minPitch={-45}
          vaov={135}
          showControls={false}
          disableKeyboardCtrl={true}
          // hotspotDebug={true}
          width="100%"
          {...pannellumOptions}
        >
          {hotspots.map(({ id, pitch, title, yaw, type }) => {
            const cbArg: PannellumCallbackArg = { id, title, type };

            return (
              <Pannellum.Hotspot
                handleClick={onHotspotClick}
                handleClickArg={cbArg}
                key={id}
                pitch={pitch}
                text={title}
                tooltip={customHotspot}
                tooltipArg={cbArg}
                type="custom"
                yaw={yaw}
              />
            );
          })}
        </Pannellum>
        {initialDrawingId !== selectedDrawingId && (
          <div className={styles['c-drawing-pannellum__back-to']}>
            <Button
              type="button"
              tag="button"
              variant="secondary"
              prefix="ArrowSmLeftIcon"
              onClick={onBackToClick}
            >
            Terug naar schip
            </Button>
          </div>
        )}
      </div>
      <Drawer
        isOpen={drawingInfoShowing}
        onClose={toggleDrawingInfo}
        position="left"
        title={{
          size: 'h3',
          text: selectedDrawing.title,
        }}
        smallPadding
      >
        <Paragraph small>{selectedDrawing.description}</Paragraph>
      </Drawer>
    </React.Fragment>
  );
};
